import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { HIDE_HOME_LOANS } from "@/config/features";

type FooterLink = { label: string; href: string };

const linkColumns: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Car Loans",
    links: [
      { label: "New Car Loans", href: "/car-loans/new-car-loans" },
      { label: "Used Car Loans", href: "/car-loans/used-car-loans" },
      { label: "Electric Vehicle Loans", href: "/calculators/car-loans/electric-vehicle-loan" },
      { label: "Refinance Car Loans", href: "/car-loans/refinance-car-loans" },
      { label: "Bad Credit Car Loans", href: "/car-loans/bad-credit-car-loans" },
      { label: "Novated Lease", href: "/car-loans/novated-lease" },
      { label: "Electric & Green Car Loans", href: "/car-loans/electric-car-loans" },
      { label: "See all car loans →", href: "/car-loans" },
    ],
  },
  ...(HIDE_HOME_LOANS ? [] : [{
    heading: "Home Loans",
    links: [
      { label: "Refinance Home Loans", href: "/home-loans/refinance" },
      { label: "First Home Buyer", href: "/home-loans/first-home-buyer" },
      { label: "Variable Rate Home Loans", href: "/home-loans/variable-rate" },
      { label: "Fixed Rate Home Loans", href: "/home-loans/fixed-rate" },
      { label: "Investment Property Loans", href: "/home-loans/investor" },
      { label: "Bad Credit Home Loans", href: "/home-loans/bad-credit" },
      { label: "Low Doc Home Loans", href: "/home-loans/low-doc" },
      { label: "See all home loans →", href: "/home-loans" },
    ],
  }]),
  {
    heading: "Personal Loans",
    links: [
      { label: "Debt Consolidation Loans", href: "/personal-loans/debt-consolidation-loans" },
      { label: "Bad Credit Personal Loans", href: "/personal-loans/bad-credit-loans" },
      { label: "Low Interest Personal Loans", href: "/personal-loans/low-interest-loans" },
      { label: "Unsecured Personal Loans", href: "/personal-loans/unsecured-loans" },
      { label: "Secured Personal Loans", href: "/personal-loans/secured-loans" },
      { label: "Fast Loans", href: "/personal-loans/fast-personal-loans" },
      { label: "Home Renovation Loans", href: "/personal-loans/renovation-loans" },
      { label: "See all personal loans →", href: "/personal-loans" },
    ],
  },
  {
    heading: "Business Loans",
    links: [
      { label: "Business Term Loans", href: "/business-loans/business-term-loans" },
      { label: "Unsecured Business Loans", href: "/business-loans/unsecured" },
      { label: "Equipment Finance", href: "/business-loans/equipment-finance" },
      { label: "Business Line of Credit", href: "/business-loans/business-overdraft" },
      { label: "Startup Business Loans", href: "/business-loans/startup-business-loans" },
      { label: "Bad Credit Business Loans", href: "/business-loans/bad-credit-business-loans" },
      { label: "Invoice Finance", href: "/business-loans/invoice-finance" },
      { label: "See all business loans →", href: "/business-loans" },
    ],
  },
  {
    heading: "Calculators",
    links: [
      { label: "Car Loan Calculator", href: "/calculators/car-loans" },
      ...(HIDE_HOME_LOANS ? [] : [
        { label: "Home Loan Calculator", href: "/calculators/home-loans" },
        { label: "Borrowing Power Calculator", href: "/calculators/home-loans/borrowing-power" },
      ]),
      { label: "Personal Loan Calculator", href: "/calculators/personal-loans" },
      { label: "Business Loan Calculator", href: "/calculators/business-loans" },
      { label: "See all calculators →", href: "/calculators" },
    ],
  },
];

const companyLinks: FooterLink[] = [
  { label: "About Us", href: "/about" },
  { label: "Guides", href: "/guides" },
  { label: "Contact", href: "/contact" },
  { label: "FAQ", href: "/faq" },
];

const legalLinks: FooterLink[] = [
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Use", href: "/terms-of-use" },
  { label: "Credit Guide", href: "/credit-guide" },
];

function FooterColumn({
  heading,
  children,
  openMap,
  setOpenMap,
}: {
  heading: string;
  children: React.ReactNode;
  openMap: Record<string, boolean>;
  setOpenMap: (m: Record<string, boolean>) => void;
}) {
  const isOpen = !!openMap[heading];
  const slug = heading.toLowerCase().replace(/\s+/g, "-");
  const panelId = `footer-${slug}-panel`;
  const headingId = `footer-${slug}-heading`;
  return (
    <div>
      {/* Mobile: real disclosure button toggling panel visibility */}
      <button
        type="button"
        onClick={() => setOpenMap({ ...openMap, [heading]: !isOpen })}
        className="flex w-full items-center justify-between mb-3 md:hidden"
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span
          id={`${headingId}-mobile`}
          className="font-display text-sm font-semibold uppercase tracking-wider text-primary-foreground/80"
        >
          {heading}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {/* Desktop: static heading, no button semantics */}
      <h4
        id={headingId}
        className="hidden md:block mb-3 font-display text-sm font-semibold uppercase tracking-wider text-primary-foreground/80"
      >
        {heading}
      </h4>
      <div
        id={panelId}
        className={`${isOpen ? "block" : "hidden"} md:block`}
      >
        {children}
      </div>
    </div>
  );
}

export function Footer() {
  const [calcOpen, setCalcOpen] = useState(false);
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  return (
    <footer className="border-t bg-primary text-primary-foreground">
      <div className="container py-12">
        <div className="grid gap-8 md:grid-cols-3 xl:grid-cols-[1.7fr_repeat(6,1fr)]">
          <div className="md:col-span-3 xl:col-span-1 md:order-last xl:order-none md:text-center xl:text-left">
            <Link to="/" className="mb-4 inline-block">
              <Logo inverted />
            </Link>
            <p className="text-sm text-primary-foreground/85">
              Helping Australians compare car loans, home loans, personal loans and business
              finance — 130+ lenders, no credit impact, free.
            </p>
          </div>

          {linkColumns.map(({ heading, links }) => (
            <FooterColumn key={heading} heading={heading} openMap={openMap} setOpenMap={setOpenMap}>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </FooterColumn>
          ))}

          <FooterColumn heading="Company" openMap={openMap} setOpenMap={setOpenMap}>
            <ul className="space-y-2">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="mt-5 mb-3 font-display text-sm font-semibold uppercase tracking-wider text-primary-foreground/80">
              Legal
            </h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterColumn>
        </div>

        <div className="mt-10 border-t border-primary-foreground/10 pt-6 space-y-4">
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            * Lender panel size varies by product category. The number of lenders available differs
            across car loans, home loans, personal loans and business finance. All panel sizes are
            indicative as of May 2026 and subject to change.
          </p>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            At loangorilla.com.au, we take your privacy and security seriously. We only collect the
            information we need to compare loans and help you with your enquiry, and we handle
            that information in line with our{" "}
            <Link to="/privacy-policy" className="underline hover:text-primary-foreground/85">Privacy Policy</Link>.
          </p>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            By using our website, calculators and comparison tools, you agree to our{" "}
            <Link to="/terms-of-use" className="underline hover:text-primary-foreground/85">Terms of Use</Link>.
            These explain how we provide our services as a comparison broker, how we're paid, what
            we're responsible for, and what you agree to when you use our site.
          </p>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            We recommend you read both documents in full before you apply for any loan or make a
            decision based on information on this site.
          </p>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            loangorilla.com.au does not compare all products or issuers available in Australia. We are not a
            broker or credit provider when we provide information via this website, we are not
            providing you with a recommendation or suggestion about a particular credit product. The
            information on this website is general in nature and does not constitute financial advice.
          </p>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            <strong>^Comparison rate warning</strong>
            <br />
            For car and personal loans, the comparison rate is based on a secured loan of $30,000
            repaid over 60 months. For home loans, the comparison rate is based on a secured loan
            of $150,000 repaid over 25 years. Terms, conditions and credit criteria apply. Fees and
            charges for late or defaulted payments may apply. WARNING: The comparison rates shown
            are true only for the examples given and may not include all fees and charges.
            Different terms, fees or other loan amounts might result in a different comparison rate.
          </p>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            <strong>*Comparing with our Lending Partners</strong>
            <br />
            If you would like a personalised loan comparison, we will match you with loangorilla.com.au
            Lending Partners based on the information you provide us. This won't affect your credit
            score. Some lenders displayed in our comparison table are not current loangorilla.com.au partners
            and we can't guarantee rates from a specific lender.
          </p>
          <div>
            <button
              type="button"
              onClick={() => setCalcOpen(!calcOpen)}
              className="flex items-center gap-1 text-xs font-semibold text-primary-foreground/80 hover:text-primary-foreground/80 transition-colors"
              aria-expanded={calcOpen}
              aria-controls="footer-calculator-disclaimer-panel"
            >
              <span>Calculator Disclaimer</span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform duration-200 ${calcOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
              <span className="sr-only">{calcOpen ? "(expanded)" : "(collapsed)"}</span>
            </button>
            {calcOpen && (
              <div
                id="footer-calculator-disclaimer-panel"
                role="region"
                aria-label="Calculator disclaimer"
                className="mt-2 space-y-3 text-xs leading-relaxed text-primary-foreground/90"
              >
                <p>
                  The calculators provided on loangorilla.com.au is intended for informational and illustrative purposes only. The results generated by these calculators are based on the inputs you provide and the assumptions set by us. These results should not be considered as financial advice or a recommendation to buy or sell any financial product. By using any of these calculators, you acknowledge and agree to the terms set out in this disclaimer. For more detailed information, please review our full terms and conditions.
                </p>
                <div>
                  <p className="font-semibold text-primary-foreground/80 mb-1">Assumptions:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>The calculations do not account for changes in interest rates or other market conditions that may occur.</li>
                    <li>Results are approximations and may differ from actual payment schedules or amounts.</li>
                    <li>The calculator does not include all fees and charges that you may incur in relation to a financial product.</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-primary-foreground/80 mb-1">Limitation</p>
                  <p>This calculator does not guarantee the availability of any financial product or the accuracy of the calculations. Please consult a financial advisor or the relevant product provider to obtain specific advice tailored to your circumstances.</p>
                </div>
                <p>loangorilla.com.au does not accept any liability for errors or omissions, or for any loss you may suffer as a result of relying on these calculations.</p>
              </div>
            )}
          </div>
          <p className="text-xs leading-relaxed text-primary-foreground/90">
            loangorilla.com.au is an ACR of the Access Lending Group credit license 531308. AFCA: 120646. © 2026 Loan Gorilla. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
